import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { chime, unlockChime } from '@/lib/chime';
import { useDocumentVisible } from '@/lib/use-document-visible';
import { nativeBadge, nativeNotifications } from '@/features/location/native-bridge';
import {
  CHAT_NOTICE_CHANNEL,
  CHAT_NOTICE_ID,
  clearNotice,
  ensureNoticeChannel,
} from './chat-notices';
import { decideAlert } from './alert-decision';
import { useChatStatus } from './chat-api';

/**
 * What the phone does when a chat message arrives and nobody is reading.
 *
 * Rides the badge poll (`useChatStatus`, every thirty seconds, kept going
 * while the page is hidden) rather than the conversation poll: that one
 * runs only with a conversation open, and a message that matters most is
 * the one that arrives while you are on some other screen. The poll now
 * carries `latest` — when the newest unread line was written — and this
 * rings when that moves forward.
 *
 * Three layers, each doing nothing where it cannot work:
 *  - a chime and a vibration, in any browser after the first tap;
 *  - a notification in the tray when the page is hidden, in the Android
 *    shell from v1.4 (older shells and browsers have no plugin: null);
 *  - a number on the launcher icon, where the launcher shows one.
 *
 * The sound is skipped when the conversation the line landed in is the one
 * on screen — there the line is already in front of the person and the read
 * marker clears the count within seconds. Another conversation still rings.
 * And nothing rings on the first answer after loading the page, which would
 * announce every message that arrived while the app was closed.
 *
 * The two channels keep separate memories on purpose. Tying the tray notice
 * to the chime meant the whole decision was made in the single poll tick
 * where `latest` moved: if that tick happened to land while the app was on
 * screen, the line was marked as handled and no notice was ever raised —
 * not then, and not when the phone was locked a minute later with the
 * message still unread. That is the shape of "no notification appeared".
 */
export function ChatAlerts() {
  const { enabled, unread, latest, latestThreadId, ready } = useChatStatus();
  const visible = useDocumentVisible();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // Held in a ref so the setup effect below can depend on nothing: react-router
  // hands back a new `navigate` on every route change, which would otherwise
  // tear down and rebuild the tap listener — and re-ask for permission — each
  // time somebody moved between screens.
  const goTo = useRef(navigate);
  goTo.current = navigate;
  /** `undefined` until the first answer; then the newest `latest` chimed for. */
  const chimed = useRef<string | null | undefined>(undefined);
  /** The newest `latest` a tray notice has been raised for. */
  const noticed = useRef<string | null>(null);

  // Sound needs a gesture first. One listener, removed after the first tap or key.
  useEffect(() => {
    const unlock = () => {
      unlockChime();
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  // In the shell: make the channel, ask for the tray if it has never been
  // asked (Android 13+ makes it a runtime permission), and open the chat when
  // a notice is tapped. Asking again once refused does nothing — Android
  // stops showing the dialog — which is why the dashboard carries a card that
  // says so and offers the way back through the phone's own settings.
  useEffect(() => {
    const plugin = nativeNotifications();
    if (!plugin) return;
    void (async () => {
      await ensureNoticeChannel();
      try {
        const state = await plugin.checkPermissions();
        if (state.display !== 'granted') await plugin.requestPermissions();
      } catch {
        // An older shell without the plugin's permission calls.
      }
    })();

    const handle = plugin.addListener('localNotificationActionPerformed', (event) => {
      void goTo.current(event.notification.extra?.link ?? '/chat');
    });
    return () => {
      void Promise.resolve(handle)
        .then((listener) => listener.remove())
        .catch(() => undefined);
    };
  }, []);

  // The launcher number follows the count, including back down to nothing —
  // and so does the tray notice, so reading the thread at a desk takes it off
  // the phone rather than leaving something to dismiss by hand. Chat being
  // switched off, or the reader no longer working here, counts as nothing:
  // the alternative is a number on the icon that nothing can ever clear.
  useEffect(() => {
    if (!ready) return;
    const nothing = !enabled || unread === 0;
    const badge = nativeBadge();
    if (badge) {
      void (nothing ? badge.clear() : badge.set({ count: unread })).catch(() => undefined);
    }
    if (nothing) {
      void clearNotice();
      noticed.current = null;
    }
  }, [ready, enabled, unread]);

  // What to announce is decided by `decideAlert`, which is a pure function
  // with a spec: the awkward sequences here — a line arriving while another
  // conversation is open, the page hiding a minute later, a thread whose only
  // unread line is a rename — are written down as tests rather than reasoned
  // about each time somebody edits this. All this effect does is carry the
  // memory across renders and perform what it is told.
  useEffect(() => {
    const decision = decideAlert(
      { ready, enabled, latest, latestThreadId, unread, visible, pathname },
      { chimed: chimed.current, noticed: noticed.current },
    );
    chimed.current = decision.memory.chimed;
    noticed.current = decision.memory.noticed;

    if (decision.chime) {
      chime();
      // Ignored by the browser while the page is hidden, where the tray
      // notice's own channel does the buzzing instead.
      navigator.vibrate?.([120, 60, 120]);
    }

    if (decision.notice) {
      void nativeNotifications()
        ?.schedule({
          notifications: [
            {
              id: CHAT_NOTICE_ID,
              channelId: CHAT_NOTICE_CHANNEL,
              title: 'KioBytes Telecom',
              body: unread === 1 ? 'New message in the chat' : `New messages in ${unread} chats`,
              extra: { link: '/chat' },
            },
          ],
        })
        .catch(() => undefined);
    }
  }, [ready, enabled, latest, latestThreadId, unread, visible, pathname]);

  return null;
}
